import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  createEtablissementPhotos,
  deleteEtablissementPhoto,
  getMyEtablissement,
  updateEtablissementPhotos,
} from "../../store/slices/etablissementSlice";
import LayoutPresta from "./layoutPresta";
import { PhotoUploadForm } from "../../components/etablissement/photo";
import { useSelector } from "react-redux";
import { Helmet, HelmetProvider } from "react-helmet-async";

const MAX_FILES = 10;
const MAX_SIZE_MB = 2;
const ALLOWED_FORMATS = [
  'image/jpeg',
  'image/jpg',
  'image/pjpeg',
  'image/png',
  'image/x-png'
];

const Photos = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [photos, setPhotos] = useState([]);
  const [error, setError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const { selectedEtablissement, isLoading, createStatus } = useSelector(
    (state) => state.etablissements
  );
  
  // Référence pour suivre les photos précédentes
  const prevPhotosRef = useRef();
  
  useEffect(() => {
    dispatch(getMyEtablissement());
  }, [dispatch]);

  // Fonction améliorée de vérification du type de fichier
  const isValidImageFile = useCallback((file) => {
    const mimeType = file.type.toLowerCase();
    const fileExtension = file.name.split('.').pop().toLowerCase();
    
    return (
      ALLOWED_FORMATS.includes(mimeType) ||
      (mimeType.startsWith('image/') && ['jpeg', 'jpg', 'png'].includes(fileExtension))
    );
  }, []);

  // Helper function to get image dimensions
  const getImageDimensions = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = function() {
        resolve({ width: this.width, height: this.height });
        URL.revokeObjectURL(this.src);
      };
      img.onerror = function() {
        reject(new Error('Failed to load image'));
        URL.revokeObjectURL(this.src);
      };
      img.src = URL.createObjectURL(file);
    });
  }, []);

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    
    let newError = "";
    setIsProcessing(true);
    
    // Créer un tableau pour stocker les nouvelles photos
    const newPhotos = [];
    
    // Process files sequentially
    for (const file of files) {
      // Check if we've already reached max files
      if (photos.length + newPhotos.length >= MAX_FILES) {
        newError = `Vous ne pouvez ajouter que ${MAX_FILES} photos au maximum.`;
        break;
      }

      if (!isValidImageFile(file)) {
        newError = `Le fichier ${file.name} n'est pas un JPG/PNG valide`;
        continue;
      }

      // Check file size
      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        newError = "La taille maximale par image est de 2 Mo.";
        continue;
      }

      // Check image dimensions
      try {
        const dimensions = await getImageDimensions(file);
        if (dimensions.width > 1920 || dimensions.height > 1080) {
          newError = "Les dimensions maximales sont 1920x1080 px.";
          continue;
        }
      } catch (error) {
        newError = "Impossible de lire les dimensions de l'image.";
        continue;
      }

      // If all checks pass, add to newPhotos array
      const newPhoto = {
        id: `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: file.name,
        preview: URL.createObjectURL(file),
        file: file,
        order: photos.length + newPhotos.length + 1,
      };
      
      newPhotos.push(newPhoto);
    }

    // Mettre à jour l'état une seule fois avec toutes les nouvelles photos
    if (newPhotos.length > 0) {
      setPhotos(prevPhotos => [...prevPhotos, ...newPhotos]);
    }
      console.log(newPhotos,photos)
    setError(newError);
    setIsProcessing(false);
    
    // Clear the input to allow selecting the same file again
    event.target.value = '';
  };

  const handleDelete = useCallback((data) => {
    if (data.url_photo) {
      dispatch(deleteEtablissementPhoto(data.id));
    }

    setPhotos(prevPhotos => {
      const newPhotos = prevPhotos
        .filter((photo) => photo.id !== data?.id)
        .map((photo, index) => ({
          ...photo,
          order: index + 1,
        }));
      return newPhotos;
    });
  }, [dispatch]);

  const handleDragEnd = useCallback((result) => {
    if (!result.destination) return;

    setPhotos(prevPhotos => {
      const items = Array.from(prevPhotos);
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);

      // Update order property based on new position
      const reorderedPhotos = items.map((item, index) => ({
        ...item,
        order: index + 1,
      }));

      return reorderedPhotos;
    });
  }, []);

  const handleSubmit = async () => {
    if (photos.length === 0) {
      setError("Veuillez ajouter au moins une photo.");
      return;
    }
    
    const formData = new FormData();
    const updates = [];
    
    photos.forEach((photo) => {
      if (photo.url_photo) {
        updates.push({
          id: photo.id,
          sort: photo.order
        });
      } else {
        formData.append(`new_photos[]`, photo.file);
        formData.append(`new_photos_sort[]`, photo.order);
      }
    });
    
    if (updates.length > 0) {
      formData.append(`updates_json`, JSON.stringify(updates));
    }

    try {
      await dispatch(updateEtablissementPhotos(formData)).unwrap();
      const stepsStatus = JSON.parse(localStorage.getItem("stepsStatus")) || {};
      stepsStatus[4] = "completed";
      localStorage.setItem("stepsStatus", JSON.stringify(stepsStatus));
      navigate("/services");
    } catch (error) {
      setError("Une erreur s'est produite lors de l'envoi des photos.");
    }
  };

  useEffect(() => {
    if (selectedEtablissement && selectedEtablissement.photos) {
      setPhotos(selectedEtablissement.photos.map(photo => ({
        ...photo,
        // Make sure each photo has a unique ID if it doesn't already
        id: photo.id || `existing-${photo.url_photo}`
      })));
    }
  }, [selectedEtablissement]);

  const isSubmitting = createStatus === 'loading';

  return (
    <LayoutPresta
      currentStep={4}
      nextStep={"/services"}
      handleSubmit={handleSubmit}
      disabled={isSubmitting || isProcessing}
      btn={false}
    >
      <HelmetProvider>
        <Helmet>
          <title>Inscription Prestataire</title>
          <meta
            name="description"
            content="Inscription Prestataire étape photos"
          />
        </Helmet>
      </HelmetProvider>
      
      <div className="col-md-6 mx-auto mt-48">
        <PhotoUploadForm
          selectedEtablissement={selectedEtablissement}
          photos={photos}
          maxFiles={MAX_FILES}
          maxSizeMB={MAX_SIZE_MB}
          allowedFormats={ALLOWED_FORMATS}
          onFileChange={handleFileChange}
          onDelete={handleDelete}
          onDragEnd={handleDragEnd}
          error={error}
          isLoading={isProcessing}
        />

        <div className="ht-100"></div>
      </div>
    </LayoutPresta>
  );
};

export default Photos;